import { AirplaneTakeoffIcon } from '@phosphor-icons/react';
import {
  fmtMoneyWhole,
  fmtTime,
  fmtWeekdayDayMonth,
} from '../../../flight/results/flight_format.js';
import {
  carrierNameOf,
  type FlightSegmentSource,
} from '../../../flight/results/flight_offer_source.js';
import { AirlineLogo } from '../../../shared/airline_logo/airline_logo.js';
import type { CheckoutMoney, FlightItemSource } from '../../model/checkout_source.js';
import './summary_legs.css';

interface SummaryLeg {
  key: string;
  segment: FlightSegmentSource;
  /** Preço da perna — só no multi-trecho (1 item = 1 perna). No RT o fio não
   *  divide o preço por perna, e inventar metade seria mentira. */
  price?: CheckoutMoney;
  fareClass?: string;
}

/** Achata o carrinho de voo em pernas na ordem de exibição: multi-trecho = a
 *  ida de cada item; RT = ida + volta do único item. */
function flightLegs(items: readonly FlightItemSource[]): SummaryLeg[] {
  const multi = items.length > 1;
  return items.flatMap((item) => {
    const base = item.detail.fareClass ? { fareClass: item.detail.fareClass } : {};
    const legs: SummaryLeg[] = [
      {
        key: `${item.offerId}:out`,
        segment: item.detail.outbound,
        ...base,
        ...(multi ? { price: item.price } : {}),
      },
    ];
    if (item.detail.return) {
      // A volta vendida à parte tem a tarifa dela (#3844); sem ela, a do itinerário.
      const fareClass = item.detail.returnFareClass ?? item.detail.fareClass;
      legs.push({
        key: `${item.offerId}:ret`,
        segment: item.detail.return,
        ...(fareClass ? { fareClass } : {}),
      });
    }
    return legs;
  });
}

/** As pernas do voo no cartão "Sua viagem", agrupadas pelo DIA de partida (Figma
 *  405-5518): label "Seg, 17 set" + trilho (avião + linha) + um cartão por perna. */
export function FlightSummaryLegs({
  items,
  locale,
}: {
  readonly items: readonly FlightItemSource[];
  readonly locale: string;
}) {
  const groups: { day: string; legs: SummaryLeg[] }[] = [];
  for (const leg of flightLegs(items)) {
    const day = fmtWeekdayDayMonth(leg.segment.departAt.slice(0, 10), locale);
    const last = groups.at(-1);
    if (last && last.day === day) last.legs.push(leg);
    else groups.push({ day, legs: [leg] });
  }
  return (
    <>
      {groups.map((group) => (
        <div className="tsp-day" key={`${group.day}:${group.legs[0]?.key ?? ''}`}>
          <span className="tsp-day-label">{group.day}</span>
          {group.legs.map((leg) => (
            <div className="tsp-leg" key={leg.key}>
              <span className="tsp-rail" aria-hidden="true">
                <AirplaneTakeoffIcon size={16} aria-hidden />
              </span>
              <div className="tsp-leg-card">
                <div className="tsp-leg-top">
                  <AirlineLogo
                    iata={leg.segment.carrier}
                    {...(leg.segment.carrierName ? { name: leg.segment.carrierName } : {})}
                    size={16}
                  />
                  <span className="tsp-leg-route">
                    {leg.segment.from} → {leg.segment.to}
                  </span>
                  {leg.price && <span className="tsp-leg-price">{fmtMoneyWhole(leg.price)}</span>}
                </div>
                <div className="tsp-leg-times">
                  {fmtTime(leg.segment.departAt)} → {fmtTime(leg.segment.arriveAt)}
                  {leg.segment.durationLabel && ` · ${leg.segment.durationLabel}`}
                </div>
                <div className="tsp-leg-fare">
                  {[
                    `${carrierNameOf(leg.segment)} ${leg.segment.flightNumber}`.trim(),
                    leg.fareClass,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}
